import React, { Component } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@material-ui/core";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";

import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import Card from "components/Card/Card.jsx";
import CardBody from "components/Card/CardBody.jsx";

import { organizationReportService } from "_services/organizationReport.service";
import { userService } from "_services/user.service";
import SearchableSelect from "components/SearchableSelect/SearchableSelect";
import "./organizationReport.css";

// Sections shown for a real organization report — mirrors the Org Admin Executive Dashboard.
// The MaxAlly section is now backed by real first-party metrics (data.maxAlly); the demo-only
// hero / $-ROI blocks with hardcoded sample numbers remain excluded.
const ORG_SECTIONS = ["kpis", "funnel", "facilities", "activity", "backlog", "speed", "alerts", "maxally"];

// Group levels (backend groupType enum) with UI labels for the Group Type selector.
const GROUP_TYPES = [
  { value: "EXECUTIVE", label: "Executive" },
  { value: "SVPO", label: "SVPO group" },
  { value: "VPO", label: "VPO group" },
  { value: "ADMIN", label: "Admin group" },
];

const TAILWIND_CDN_ID = "tailwind-cdn-orgreport";

export function injectTailwindCdn() {
  if (document.getElementById(TAILWIND_CDN_ID)) return;
  // Disable Tailwind preflight so it doesn't reset MUI's global styles on other pages.
  const cfg = document.createElement("script");
  cfg.id = `${TAILWIND_CDN_ID}-cfg`;
  cfg.text = "window.tailwind = window.tailwind || {}; window.tailwind.config = { corePlugins: { preflight: false } };";
  document.head.appendChild(cfg);

  const s = document.createElement("script");
  s.id = TAILWIND_CDN_ID;
  s.src = "https://cdn.tailwindcss.com";
  document.head.appendChild(s);

  if (!document.getElementById("inter-font-orgreport")) {
    const link = document.createElement("link");
    link.id = "inter-font-orgreport";
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap";
    document.head.appendChild(link);
  }
}

export default class OrganizationReport extends Component {
  constructor(props) {
    super(props);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    thirtyDaysAgo.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.state = {
      groups: [], // all groups (flat) for the dropdowns
      groupType: "", // selected Group Type (EXECUTIVE/SVPO/VPO/ADMIN)
      groupId: "", // selected group id
      startDate: thirtyDaysAgo,
      endDate: today,
      loading: false,
      data: null,
      error: null,
      hasGenerated: false,
    };
  }

  componentDidMount() {
    injectTailwindCdn();
    this.loadGroups();
  }

  loadGroups = async () => {
    try {
      const groups = await organizationReportService.fetchGroups();
      this.setState({ groups: Array.isArray(groups) ? groups : [] });
    } catch (e) {
      this.setState({ groups: [] });
    }
  };

  selectedGroupName = () => {
    const g = this.state.groups.find((x) => x.id === this.state.groupId);
    return g ? g.groupName : "Group";
  };

  // Generate the report for the selected group in one call — the backend resolves the group's
  // whole subtree (GROUP + DIRECT facilities, recursively) and runs the shared report engine.
  handleGenerate = async () => {
    const { startDate, endDate, groupType, groupId } = this.state;
    if (!groupType) {
      userService.showNotification({ title: "Executive Dashboard", message: "Please select a Group Type.", type: "danger" });
      return;
    }
    if (!groupId) {
      userService.showNotification({ title: "Executive Dashboard", message: "Please select a Group.", type: "danger" });
      return;
    }
    if (!startDate || !endDate) {
      userService.showNotification({ title: "Executive Dashboard", message: "Please select a valid date range.", type: "danger" });
      return;
    }
    this.setState({ loading: true, error: null, hasGenerated: true });
    try {
      const data = await organizationReportService.fetchGroupReport({ groupId, startDate, endDate });
      this.setState({ data, loading: false });
    } catch (e) {
      this.setState({ error: e.message, loading: false, data: null });
    }
  };

  render() {
    const { startDate, endDate, groupType, groupId, groups, loading, data, hasGenerated, error } =
      this.state;
    const groupName = this.selectedGroupName();
    const groupsForType = groupType ? groups.filter((g) => g.groupType === groupType) : [];

    const kpis = (data && data.networkKpis) || {};
    const funnel = (data && data.funnel) || {};
    const activity = (data && data.activityByDay) || [];
    const backlog = (data && data.canAcceptBacklog) || [];
    const facilities = (data && data.facilities) || [];
    const speed = (data && data.speedDistribution) || {};
    const alerts = (data && data.criticalAlerts) || [];
    const maxAlly = (data && data.maxAlly) || {};
    const noActivity = hasGenerated && data && !kpis.totalSends && !kpis.uniqueReferrals;

    return (
      <div className="orgreport-root">
        <GridContainer>
          <GridItem xs={12}>
            <Card>
              <CardBody>
                <Box display="flex" flexWrap="wrap" gridGap={16} alignItems="center" mb={2}>
                  <FormControl style={{ minWidth: 200 }}>
                    <InputLabel id="group-type-label" shrink>Group Type</InputLabel>
                    <Select
                      labelId="group-type-label"
                      value={groupType}
                      displayEmpty
                      onChange={(e) =>
                        this.setState({
                          groupType: e.target.value,
                          groupId: "",
                          hasGenerated: false,
                          data: null,
                        })
                      }
                    >
                      <MenuItem value="" disabled>
                        Select a Group Type
                      </MenuItem>
                      {GROUP_TYPES.map((t) => (
                        <MenuItem key={t.value} value={t.value}>
                          {t.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <div style={{ minWidth: 220 }}>
                    <SearchableSelect
                      label="Group"
                      placeholder="Select a Group"
                      shrinkLabel
                      value={groupId}
                      disabled={!groupType}
                      onChange={(v) => this.setState({ groupId: v, hasGenerated: false, data: null })}
                      noOptionsText={groupType ? "No groups of this type" : "Select a group type first"}
                      options={groupsForType.map((g) => ({ value: g.id, label: g.groupName }))}
                    />
                  </div>
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <KeyboardDatePicker
                      label="Start Date"
                      format="MM/dd/yyyy"
                      value={startDate}
                      onChange={(d) => this.setState({ startDate: d })}
                      maxDate={endDate || new Date()}
                    />
                    <KeyboardDatePicker
                      label="End Date"
                      format="MM/dd/yyyy"
                      value={endDate}
                      onChange={(d) => this.setState({ endDate: d })}
                      minDate={startDate}
                      maxDate={new Date()}
                    />
                  </MuiPickersUtilsProvider>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={this.handleGenerate}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={20} /> : "Generate Report"}
                  </Button>
                  {/* Lives in the toolbar, not in the report body — the body scrolls. */}
                  {data && !loading ? <PrintReportButton /> : null}
                </Box>

                {error ? (
                  <Box mb={2} style={{ color: "#dc2626", fontSize: "0.85rem" }}>
                    {error}
                  </Box>
                ) : null}

                {!hasGenerated ? (
                  <Box
                    py={6}
                    textAlign="center"
                    style={{ color: "#6b6969", fontSize: "0.9rem" }}
                  >
                    Select a Group Type and Group and date range, then click{" "}
                    <strong>Generate Report</strong> to view the dashboard.
                  </Box>
                ) : loading ? (
                  /* Wait for the API before rendering any report elements — avoids the flash of
                     undefined/zero values while the request is in flight. */
                  <Box
                    py={8}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    style={{ color: "#6b6969" }}
                  >
                    <CircularProgress />
                    <Box mt={2} style={{ fontSize: "0.9rem" }}>
                      Generating report…
                    </Box>
                  </Box>
                ) : data ? (
                  /* All Tailwind-styled markup is scoped under .orgreport-body to limit reset/utility impact */
                  <div className="orgreport-body">
                    {noActivity ? (
                      <Box mb={2} style={{ color: "#7b7979", fontSize: "0.85rem" }}>
                        No referral activity across <strong>{groupName}</strong> in the selected date range.
                      </Box>
                    ) : null}
                    <ReportBody
                      organization={{ name: groupName }}
                      window={{ startDate, endDate }}
                      kpis={kpis}
                      funnel={funnel}
                      activity={activity}
                      backlog={backlog}
                      facilities={facilities}
                      speed={speed}
                      alerts={alerts}
                      maxAlly={maxAlly}
                      sections={ORG_SECTIONS}
                      footer={false}
                    />
                  </div>
                ) : null}
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      </div>
    );
  }
}

/* ------------------------------------------------------------------ *
 *  REPORT BODY — port of newgen-executive-dashboard-v4-2026-05-14.html
 *  Inline styles + Tailwind utility classes match the original mock.
 * ------------------------------------------------------------------ */

export function ReportBody({
  organization,
  window: dateWindow,
  kpis,
  funnel,
  activity,
  backlog,
  facilities,
  speed,
  alerts,
  maxAlly,
  sections,
  footer = true,
}) {
  // `sections` (optional) restricts which blocks render; default = all (admin dashboard unchanged).
  const show = (key) => !sections || sections.indexOf(key) > -1;
  const windowLabel = `${formatDate(dateWindow.startDate)} – ${formatDate(dateWindow.endDate)}`;
  return (
    <div className="orgreport-print-root" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
      {/* Paper-only title. The facility and org surfaces omit the "hero" block, so without
          this their printouts would carry no report name and no date range. */}
      <div className="orgreport-print-only" style={{ marginBottom: 16 }}>
        <div style={{ fontSize: "1.05rem", fontWeight: 700, color: "#252424" }}>
          {organization.name} · Executive Summary
        </div>
        <div style={{ fontSize: "0.8rem", color: "#7b7979", marginTop: 2 }}>{windowLabel}</div>
      </div>
      {show("hero") && (
        <Hero kpis={kpis} windowLabel={windowLabel} organization={organization} />
      )}
      {show("alerts") && (
        <div style={{ marginTop: 16 }}>
          <CriticalAlerts alerts={alerts} />
        </div>
      )}
      {show("impact") && (
        <div style={{ marginTop: 16 }}>
          <ImpactBanner kpis={kpis} windowLabel={windowLabel} />
        </div>
      )}
      {show("kpis") && (
        <div style={{ marginTop: 16 }}>
          <NetworkKpis kpis={kpis} />
        </div>
      )}
      {show("funnel") && (
        <div style={{ marginTop: 16 }}>
          <FunnelSection funnel={funnel} kpis={kpis} />
        </div>
      )}
      {show("activity") && (
        <div style={{ marginTop: 16 }}>
          <ActivityByDay activity={activity} />
        </div>
      )}
      {show("backlog") && (
        <div style={{ marginTop: 16 }}>
          <CanAcceptBacklog backlog={backlog} />
        </div>
      )}
      {show("facilities") && (
        <div style={{ marginTop: 16 }}>
          <FacilityNetwork facilities={facilities} windowLabel={windowLabel} dateWindow={dateWindow} />
        </div>
      )}
      {show("speed") && (
        <div style={{ marginTop: 16 }}>
          <SpeedToFinalize speed={speed} kpis={kpis} />
        </div>
      )}
      {show("maxally") && (
        <div style={{ marginTop: 16 }}>
          <MaxAllySection maxAlly={maxAlly} />
        </div>
      )}
      {footer && (
        <div style={{ textAlign: "center", padding: "16px 0 8px", fontSize: "0.72rem", color: "#b0aeae" }}>
          MAXMRJ Executive Dashboard · {organization.name} · {windowLabel} · Demo Preview<br />
          MaxAlly section: ⚠ MOCK/DEMO DATA · Future Organization hierarchy feature
        </div>
      )}
    </div>
  );
}

const PRINT_PORTAL_ID = "orgreport-print-portal";

/**
 * Print the report.
 *
 * The report normally sits inside a ~78vh `overflow: auto` container, and a scrolling or
 * height-constrained ancestor makes the browser print only the visible slice. So instead of
 * printing in place, the report is cloned into a portal appended directly to <body>, where it
 * has no such ancestor and paginates across as many pages as it needs. See organizationReport.css
 * for why this is NOT done with `position: absolute` (that prints exactly one page).
 */
function printReport() {
  const source = document.querySelector(".orgreport-print-root");
  if (!source) {
    window.print();
    return;
  }

  const existing = document.getElementById(PRINT_PORTAL_ID);
  if (existing && existing.parentNode) {
    existing.parentNode.removeChild(existing);
  }

  const portal = document.createElement("div");
  portal.id = PRINT_PORTAL_ID;
  const clone = source.cloneNode(true);
  // Drop screen-only controls (the Print button itself) from the printed copy.
  const omit = clone.querySelectorAll(".orgreport-no-print");
  Array.prototype.forEach.call(omit, (el) => {
    if (el.parentNode) el.parentNode.removeChild(el);
  });
  portal.appendChild(clone);
  document.body.appendChild(portal);
  document.body.classList.add("orgreport-printing");

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    document.body.classList.remove("orgreport-printing");
    const p = document.getElementById(PRINT_PORTAL_ID);
    if (p && p.parentNode) p.parentNode.removeChild(p);
    window.removeEventListener("afterprint", cleanup);
  };

  // afterprint is the reliable signal. The timeout is a fallback for browsers that don't fire
  // it — deliberately NOT a synchronous cleanup() after print(), because where print() returns
  // immediately instead of blocking, removing the portal that fast would print a blank page.
  // The portal is display:none on screen, so lingering for a moment is invisible either way.
  window.addEventListener("afterprint", cleanup);
  window.print();
  setTimeout(cleanup, 1000);
}

/** Inline printer glyph — avoids pulling an icon package in for a single button. */
function PrinterIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="6 9 6 2 18 2 18 9" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="8" />
    </svg>
  );
}

/**
 * Print control for the report surfaces.
 *
 * Rendered in the page toolbar (beside "Generate Report"), NOT inside <ReportBody>. The report body
 * is a ~78vh overflow:auto box on the org and facility surfaces, so a button placed inside it scrolls
 * away with the content; `position: sticky` only papers over that — it still releases at the bottom of
 * its containing block. Keeping the button outside the scroller is the actual fix, and it lines the
 * control up with the other page actions.
 *
 * `height: 36` matches MUI's contained Button so the two sit on the same baseline in the flex toolbar;
 * `marginLeft: auto` parks it at the far right of that row, away from the filter/Generate controls.
 * Both are overridable through `style`.
 */
export function PrintReportButton({ style }) {
  return (
    <button
      type="button"
      onClick={printReport}
      title="Print this report, or save it as a PDF"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        height: 36,
        marginLeft: "auto",
        background: "#fff",
        color: "#252424",
        border: "1px solid #e1e0e0",
        borderRadius: 8,
        padding: "0 14px",
        fontSize: "0.82rem",
        fontWeight: 600,
        fontFamily: "inherit",
        cursor: "pointer",
        ...style,
      }}
    >
      <PrinterIcon />
      Print
    </button>
  );
}

function Hero({ kpis, windowLabel, organization }) {
  return (
    <div
      style={{
        background: "linear-gradient(135deg,#252424 0%,#1e1d1d 100%)",
        borderRadius: 16,
        padding: "28px 36px",
        borderBottom: "3px solid #00a7a6",
        color: "#fff",
      }}
    >
      <div style={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "#00a7a6", marginBottom: 10 }}>
        {organization.name} · Executive Summary · {windowLabel}
      </div>
      <div style={{ fontSize: "1.3rem", fontWeight: 700, color: "#fff", lineHeight: 1.55 }}>
        At full network capacity, MAXMRJ is projected to deliver{" "}
        <span style={{ color: "#00a7a6" }}>$350,000+</span> in combined annual value —
        coordinator efficiency savings plus{" "}
        <span style={{ color: "#00a7a6" }}>$17K–$34K</span> in VBP penalty avoidance per facility
        improving 30-day readmission performance.
      </div>
      <div style={{ marginTop: 12, fontSize: "0.78rem", color: "#9b9898", lineHeight: 1.5 }}>
        Today's baseline:{" "}
        <strong style={{ color: "#f0f0f0" }}>
          {kpis.activeFacilities} active facilities · {kpis.totalSends} sends ·{" "}
          {kpis.finalized} finalized referrals · {kpis.placementRatePct}% placement rate.
        </strong>
      </div>
    </div>
  );
}

function CriticalAlerts({ alerts }) {
  if (!alerts || alerts.length === 0) {
    return (
      <div style={{ background: "#f0fff4", border: "1px solid #86efac", borderRadius: 14, padding: "16px 24px", color: "#166534", fontWeight: 600 }}>
        ✓ No coordinator-blocked Can Accept cases in this window.
      </div>
    );
  }
  return (
    <div style={{ background: "#fff0f0", border: "2px solid #fca5a5", borderRadius: 14, padding: "20px 28px" }}>
      <div style={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#dc2626", marginBottom: 12 }}>
        ⚠ Critical — Coordinator Action Required · {alerts.length}{" "}
        {alerts.length === 1 ? "Facility" : "Facilities"}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: alerts.length > 1 ? "1fr 1fr" : "1fr", gap: 20 }}>
        {alerts.map((a, i) => (
          <div key={i} style={{ background: "#fff", border: "1px solid #fca5a5", borderRadius: 10, padding: 16 }}>
            <div style={{ fontWeight: 800, color: "#dc2626", fontSize: "1rem", marginBottom: 6 }}>
              {a.facilityName}
            </div>
            <div style={{ fontSize: "0.82rem", color: "#252424", lineHeight: 1.7 }}>
              <strong style={{ color: "#dc2626" }}>
                {a.openCanAccepts} open Can Accept{a.openCanAccepts === 1 ? "" : "s"} · oldest {a.oldestWaitDays}d:
              </strong>{" "}
              {a.providers.join(" · ")}
              <div style={{ color: "#6b6969", fontSize: "0.75rem", marginTop: 4 }}>
                → coordinator finalize step pending
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ImpactBanner({ kpis, windowLabel }) {
  return (
    <div style={{ background: "linear-gradient(135deg,#f0fffe 0%,#eafffe 100%)", borderRadius: 12, padding: 20, border: "1px solid #99eeed" }}>
      <div className="section-label" style={{ marginBottom: 12 }}>
        MAXMRJ Impact · {windowLabel}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
        <KpiCard
          big="$350K+"
          label="projected annual value · full network"
          sub="coordinator savings + VBP reduction · 20 facilities"
          footnote="$16.2K projected today · current active facilities"
          topBorder
        />
        <KpiCard
          big="~1,000"
          label="est. placements/year · full network"
          sub="all 20 facilities · annualized"
          footnote={`${kpis.finalized} finalized this period · ${kpis.activeFacilities} facilities`}
        />
        <KpiCard
          big="~800"
          label="est. coordinator hrs saved/yr"
          sub="all 20 facilities · ~$28K at $35/hr equiv."
          footnote={`based on ~40 min saved per referral`}
        />
        <KpiCard
          big={
            <span>
              {kpis.activeFacilities}
              <span style={{ fontSize: "1rem", fontWeight: 700, color: "#6b6969" }}>
                {" "}/ {kpis.totalFacilities}
              </span>
            </span>
          }
          label="facilities active"
          sub={`${Math.max(kpis.totalFacilities - kpis.activeFacilities, 0)} dark / onboarding`}
        />
      </div>
    </div>
  );
}

function KpiCard({ big, label, sub, footnote, topBorder }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e1e0e0",
        borderRadius: 14,
        padding: 16,
        textAlign: "center",
        boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
        ...(topBorder ? { borderTop: "3px solid #00a7a6" } : {}),
      }}
    >
      <div style={{ fontSize: "2.1rem", fontWeight: 800, color: "#00a7a6", lineHeight: 1 }}>{big}</div>
      <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#252424", marginTop: 2 }}>{label}</div>
      <div style={{ fontSize: "0.72rem", color: "#6b6969", marginTop: 4 }}>{sub}</div>
      {footnote && (
        <div style={{ fontSize: "0.67rem", color: "#b0aeae", marginTop: 3, fontStyle: "italic" }}>
          {footnote}
        </div>
      )}
    </div>
  );
}

function NetworkKpis({ kpis }) {
  return (
    <div>
      <SectionAccent title="Network Health" sub={`${kpis.activeFacilities} active facilities`} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 16 }}>
        <SmallKpi label="Referrals Sent" value={kpis.totalSends} sub={`${kpis.uniqueReferrals} unique referral IDs`} />
        <SmallKpi label="Finalized" value={kpis.finalized} sub={`of ${kpis.uniqueReferrals} unique IDs`} accent="#16a34a" extra={`${kpis.placementRatePct}% placement rate`} />
        <SmallKpi label="Can Accept — Waiting" value={kpis.canAcceptOpen} accent="#dc2626" extra={kpis.canAcceptOpen ? "→ coordinator action needed" : ""} leftBorder="#dc2626" />
        <SmallKpi
          label="Median to Finalize"
          value={fmtMinutes(kpis.medianMinutesToFinalize)}
          sub={kpis.avgHoursToFinalize != null ? `${kpis.avgHoursToFinalize} hr average` : ""}
        />
        <SmallKpi label="Finalized <24h" value={`${kpis.pctWithin24h}%`} sub={`${kpis.pctWithin48h}% within 48h`} />
        <SmallKpi label="Active Facilities" value={kpis.activeFacilities} sub={`of ${kpis.totalFacilities} total`} />
      </div>
    </div>
  );
}

function SmallKpi({ label, value, sub, accent, extra, leftBorder }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e1e0e0",
        borderRadius: 14,
        padding: 16,
        ...(leftBorder ? { borderLeft: `3px solid ${leftBorder}` } : {}),
      }}
    >
      <div className="section-label">{label}</div>
      <div style={{ fontSize: "2.1rem", fontWeight: 800, color: accent || "#00a7a6", lineHeight: 1, marginTop: 4 }}>{value}</div>
      {sub ? <div style={{ fontSize: "0.78rem", color: "#6b6969", marginTop: 4 }}>{sub}</div> : null}
      {extra && (
        <div style={{ fontSize: "0.72rem", color: accent || "#16a34a", fontWeight: 600, marginTop: 4 }}>{extra}</div>
      )}
    </div>
  );
}

function FunnelSection({ funnel, kpis }) {
  const sent = funnel.sent || 0;
  // Same trapezoid-bar visual as the MaxAlly "Post-Discharge Prevention Funnel":
  // value + label inside each colored bar, a taper triangle below, arrows between, summary chip.
  const stages = [
    { label: "TOTAL SENDS", value: funnel.sent || 0, color: "#00a7a6", tint: "#ccfffe", width: 190 },
    { label: "ENGAGED", value: funnel.engaged || 0, color: "#00b8b7", tint: "#ccfffe", width: 160 },
    { label: "RESPONDED", value: funnel.responded || 0, color: "#00c9c8", tint: "#ccfffe", width: 130 },
    { label: "FINALIZED", value: funnel.finalized || 0, color: "#16a34a", tint: "#bbf7d0", width: 104 },
  ];
  const maxStage = Math.max(1, ...stages.map((s) => s.value));
  return (
    <Card2>
      <SectionAccent title="Referral Velocity Funnel" sub={`${funnel.sent} provider sends · ${kpis.uniqueReferrals} unique referral IDs`} />
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 0 }}>
        {stages.map((s, i) => (
          <React.Fragment key={s.label}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div
                title={`${s.label}: ${s.value}${sent ? ` · ${pctOf(s.value, sent)} of sends` : ""}`}
                style={{
                  width: s.width,
                  background: s.color,
                  height: 52 + Math.round((s.value / maxStage) * 80),
                  borderRadius: "8px 8px 0 0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <div style={{ color: "#fff", fontSize: "1.6rem", fontWeight: 800 }}>{s.value}</div>
                <div style={{ color: s.tint, fontSize: "0.72rem", fontWeight: 600 }}>{s.label}</div>
                {i > 0 && sent ? (
                  <div style={{ color: s.tint, fontSize: "0.66rem" }}>{pctOf(s.value, sent)}</div>
                ) : null}
              </div>
              <div
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: `${s.width / 2}px solid transparent`,
                  borderRight: `${s.width / 2}px solid transparent`,
                  borderTop: `18px solid ${s.color}`,
                }}
              />
            </div>
            {i < stages.length - 1 && (
              <div style={{ color: "#00a7a6", fontSize: "1.2rem", margin: "0 4px", paddingBottom: 18 }}>→</div>
            )}
          </React.Fragment>
        ))}
      </div>
      <div style={{ marginTop: 16, textAlign: "center" }}>
        <span style={{ background: "#dcfce7", color: "#15803d", borderRadius: 6, padding: "4px 12px", fontSize: "0.78rem", fontWeight: 600 }}>
          {funnel.finalized} of {kpis.uniqueReferrals} referrals finalized · {kpis.placementRatePct}% placement rate
        </span>
      </div>
    </Card2>
  );
}

function ActivityByDay({ activity }) {
  const maxSend = Math.max(1, ...activity.map((d) => d.sends || 0));
  return (
    <Card2>
      <SectionAccent title="Referral Activity by Day of Week" sub="Best ingest window: Monday morning" />
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12, height: 140, paddingBottom: 4 }}>
        {activity.map((d) => {
          const sendPct = ((d.sends || 0) / maxSend) * 100;
          const finPct = ((d.finalized || 0) / maxSend) * 100;
          return (
            <div key={d.day} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
              <div style={{ width: "100%", display: "flex", gap: 3, alignItems: "flex-end", height: 110 }}>
                <div style={{ flex: 1, background: "#00a7a6", borderRadius: "3px 3px 0 0", height: `${Math.max(sendPct, 1)}%` }} />
                <div style={{ flex: 1, background: "#16a34a", borderRadius: "3px 3px 0 0", height: `${Math.max(finPct, 1)}%` }} />
              </div>
              <div style={{ fontSize: "0.7rem", fontWeight: 600, color: "#6b6969" }}>{d.day}</div>
              <div style={{ fontSize: "0.62rem", color: "#9b9898" }}>
                {d.sends} · {d.finalized}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 14, fontSize: "0.72rem", color: "#6b6969" }}>
        <span style={{ display: "inline-block", width: 12, height: 12, background: "#00a7a6", borderRadius: 2, marginRight: 6 }} />Sends
        <span style={{ display: "inline-block", width: 12, height: 12, background: "#16a34a", borderRadius: 2, margin: "0 6px 0 12px" }} />Finalizations
      </div>
    </Card2>
  );
}

function CanAcceptBacklog({ backlog }) {
  return (
    <Card2>
      <SectionAccent title="Can Accept Backlog" sub="Providers said YES · Coordinators have not finalized · Within the selected date range" />
      {(!backlog || backlog.length === 0) ? (
        <div style={{ color: "#6b6969", padding: 12 }}>No open Can Accept entries within window.</div>
      ) : (
        <table className="orgreport-table">
          <thead>
            <tr><th>Facility</th><th>Provider</th><th style={{ textAlign: "right" }}>Sent</th><th style={{ textAlign: "right" }}>Wait</th></tr>
          </thead>
          <tbody>
            {backlog.map((row, i) => (
              <tr key={i} style={row.waitDays >= 14 ? { background: "#fff8f8" } : null}>
                <td style={{ fontWeight: 600, color: row.waitDays >= 14 ? "#dc2626" : "#d97706" }}>{row.facilityName}</td>
                <td style={{ color: "#6b6969", fontSize: "0.82rem" }}>{row.providerName}</td>
                <td style={{ textAlign: "right", color: "#6b6969", fontSize: "0.78rem" }}>{row.sentDate}</td>
                <td style={{ textAlign: "right" }}>
                  <span
                    style={{
                      background: row.waitDays >= 14 ? "#fee2e2" : "#fef3c7",
                      color: row.waitDays >= 14 ? "#991b1b" : "#92400e",
                      borderRadius: 4,
                      padding: "2px 7px",
                      fontSize: "0.72rem",
                      fontWeight: 700,
                    }}
                  >
                    {row.waitDays}d
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card2>
  );
}

// Per-facility weekly "Trend" sparkline: one bar per week (oldest -> newest, max 8). Height is
// scaled to the facility's own busiest week; teal for weeks with refs, grey for empty weeks.
function TrendSparkline({ weeks }) {
  if (!weeks || weeks.length === 0) {
    return <span style={{ color: "#b0aeae", fontSize: "0.75rem" }}>—</span>;
  }
  const max = Math.max(1, ...weeks.map((w) => w || 0));
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 3, width: 84, height: 26, margin: "0 auto" }}>
      {weeks.map((w, i) => {
        const val = w || 0;
        const heightPct = val > 0 ? Math.max((val / max) * 100, 14) : 8;
        return (
          <div
            key={i}
            title={`${val} ref${val === 1 ? "" : "s"}`}
            style={{
              flex: 1,
              height: `${heightPct}%`,
              background: val > 0 ? "#00a7a6" : "#e1e0e0",
              borderRadius: "2px 2px 0 0",
            }}
          />
        );
      })}
    </div>
  );
}

function FacilityNetwork({ facilities, windowLabel, dateWindow }) {
  const isActive = (f) => (f.uniqueRefs || 0) > 0 || (f.sends || 0) > 0;
  const activeFacilities = facilities.filter(isActive);
  const darkFacilities = facilities.filter((f) => !isActive(f));

  // Performance window = whole weeks in the selected date range, UNCAPPED (3 months -> 12, etc.).
  const rangeDays =
    dateWindow && dateWindow.startDate && dateWindow.endDate
      ? Math.round((dateWindow.endDate - dateWindow.startDate) / 86400000) + 1
      : 0;
  const performanceWeeks = Math.floor(rangeDays / 7);
  // Sparkline weeks = the per-row bar count the backend produced (capped at 8 bars).
  const sparklineWeeks = Math.max(0, ...facilities.map((f) => (f.weeklyUniqueRefs || []).length));

  const title =
    performanceWeeks > 0 ? `Facility Network — ${performanceWeeks}-Week Performance` : "Facility Network";
  const sub =
    (windowLabel ? `${windowLabel} · ` : "") +
    (sparklineWeeks > 0 ? `${sparklineWeeks}-week trend sparklines · ` : "") +
    "Hover headers for definitions";

  return (
    <Card2>
      <SectionAccent title={title} sub={sub} />
      <table className="orgreport-table">
        <thead>
          <tr>
            <th style={{ width: 30 }}>#</th>
            <th>Facility</th>
            <th
              style={{ textAlign: "center", width: 96 }}
              title="Weekly unique referral IDs — each bar = 1 week."
            >
              Trend
            </th>
            <th style={{ textAlign: "right" }} title="Unique patient referral IDs in the selected period.">
              Refs
            </th>
            <th
              style={{ textAlign: "right" }}
              title="Total provider broadcasts. One referral ID may go to many providers."
            >
              Sends
            </th>
            <th style={{ textAlign: "right" }} title="Referral IDs with a finalized outcome in the selected period.">
              Placed
            </th>
            <th style={{ width: 140 }} title="% of unique referral IDs that resulted in a successful placement.">
              Placement Rate
            </th>
            <th>Can Accept</th>
          </tr>
        </thead>
        <tbody>
          {activeFacilities.map((f, i) => {
            const rate = f.placementRatePct || 0;
            const color = rate >= 90 ? "#16a34a" : rate >= 60 ? "#00a7a6" : rate > 0 ? "#d97706" : "#dc2626";
            return (
              <tr key={f.facilityId || i}>
                <td>{rankMedal(i)}</td>
                <td style={{ fontWeight: 600 }}>{f.facilityName}</td>
                <td style={{ textAlign: "center" }}>
                  <TrendSparkline weeks={f.weeklyUniqueRefs} />
                </td>
                <td style={{ textAlign: "right" }}>{f.uniqueRefs}</td>
                <td style={{ textAlign: "right", color: "#6b6969" }}>{f.sends}</td>
                <td style={{ textAlign: "right", fontWeight: 600, color }}>{f.placed}</td>
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ height: 6, width: 80, background: "#e1e0e0", borderRadius: 999, overflow: "hidden" }}>
                      <div style={{ height: 6, width: `${rate}%`, background: color, borderRadius: 999 }} />
                    </div>
                    <span style={{ fontSize: "0.82rem", fontWeight: 600, color }}>{rate}%</span>
                  </div>
                </td>
                <td>
                  {f.openCanAccept > 0 ? (
                    <span style={{ background: "#fef3c7", color: "#92400e", borderRadius: 4, padding: "1px 6px", fontSize: "0.72rem", fontWeight: 700 }}>
                      {f.openCanAccept} CA · {f.maxCaWaitDays}d
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            );
          })}
          {darkFacilities.length > 0 && (
            <tr>
              <td colSpan={8} style={{ background: "#f4f3f1", borderTop: "2px solid #e1e0e0", borderBottom: "2px solid #e1e0e0", padding: "7px 12px" }}>
                <span style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#7b7979" }}>
                  ▼ Dark / Onboarding Facilities — No Activity in Selected Period
                </span>
              </td>
            </tr>
          )}
          {darkFacilities.map((f, i) => (
            <tr key={`dark-${f.facilityId || i}`} style={{ opacity: 0.7 }}>
              <td style={{ color: "#b0aeae" }}>—</td>
              <td style={{ color: "#6b6969" }}>{f.facilityName}</td>
              <td style={{ textAlign: "center", color: "#b0aeae" }}>—</td>
              <td colSpan={2} style={{ textAlign: "right" }}>
                <span style={{ fontSize: "0.75rem", background: "#fef3c7", color: "#92400e", borderRadius: 4, padding: "2px 6px", fontWeight: 600 }}>
                  no activity
                </span>
              </td>
              <td style={{ color: "#b0aeae" }}>—</td>
              <td style={{ color: "#b0aeae" }}>—</td>
              <td>—</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card2>
  );
}

function SpeedToFinalize({ speed, kpis }) {
  const buckets = [
    { label: "< 24 hours", value: speed.lt24h, color: "#16a34a" },
    { label: "24 – 48 hours", value: speed.h24_48, color: "#00a7a6" },
    { label: "48 hrs – 3 days", value: speed.d2_3, color: "#00a7a6" },
    { label: "3 – 7 days", value: speed.d3_7, color: "#00a7a6" },
    { label: "7 – 14 days", value: speed.d7_14, color: "#d97706" },
    { label: "14+ days", value: speed.gt14d, color: "#dc2626" },
  ];
  return (
    <Card2>
      <SectionAccent
        title="Speed to Finalize"
        sub={`${fmtMinutes(speed.medianMinutes)} median · ${speed.lt24h}% within 24 hrs`}
      />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32 }}>
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
            <BigStat value={minsNum(speed.medianMinutes)} unit={`${minsUnit(speed.medianMinutes)} median`} sub="to finalize" />
            <BigStat value={speed.avgHours ?? "—"} unit="hrs average" sub="long-tail cases" color="#252424" />
          </div>
          <ProgressLine label="Within 24 hours" value={speed.lt24h} color="#16a34a" />
          <ProgressLine label="Within 48 hours" value={kpis.pctWithin48h} color="#00a7a6" />
          <ProgressLine label="Within 7 days" value={kpis.pctWithin7d} color="#00a7a6" />
        </div>
        <div>
          <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#6b6969", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 16 }}>
            Time-to-Finalize Distribution
          </div>
          {buckets.map((b) => (
            <div key={b.label} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: "0.82rem" }}>{b.label}</span>
                <span style={{ fontSize: "0.82rem", fontWeight: 700, color: b.color }}>{b.value}%</span>
              </div>
              <div style={{ height: 10, background: "#e1e0e0", borderRadius: 999, overflow: "hidden" }}>
                <div style={{ height: 10, width: `${Math.max(b.value, 0)}%`, background: b.color, borderRadius: 999 }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card2>
  );
}

function BigStat({ value, unit, sub, color = "#00a7a6" }) {
  return (
    <div style={{ background: "#f8f8f8", borderRadius: 10, padding: 16, textAlign: "center" }}>
      <div style={{ fontSize: "2.2rem", fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: "0.78rem", color: "#6b6969", fontWeight: 600 }}>{unit}</div>
      <div style={{ fontSize: "0.7rem", color: "#6b6969", marginTop: 2 }}>{sub}</div>
    </div>
  );
}

function ProgressLine({ label, value, color }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ fontSize: "0.82rem", color: "#252424" }}>{label}</span>
        <span style={{ fontWeight: 700, color }}>{value}%</span>
      </div>
      <div style={{ height: 6, background: "#e1e0e0", borderRadius: 999, overflow: "hidden", marginTop: 4 }}>
        <div style={{ height: 6, width: `${Math.max(value, 0)}%`, background: color, borderRadius: 999 }} />
      </div>
    </div>
  );
}

/* MaxAlly — fully static / mock as required */
// MaxAlly post-discharge early-warning section, backed by real first-party metrics (data.maxAlly).
// Caregiver Activation + Check-ins Completed reflect the selected date window; the funnel, risk flags,
// flags reviewed, domain breakdown and Coverage Window are computed over all post-discharge activity.
function MaxAllySection({ maxAlly }) {
  const m = maxAlly || {};
  const ca = m.caregiverActivation || {};
  const ci = m.checkIns || {};
  const funnel = m.funnel || {};
  const flags = m.riskFlags || {};
  const reviewed = m.flagsReviewed || {};
  const coverage = m.coverage || {};
  const questions = m.questions || {};
  const domains = m.domains || [];

  const n = (v) => (v == null ? 0 : v);
  const one = (v) => (v == null ? null : Math.round(v * 10) / 10);
  const pctTxt = (v) => (v == null ? "—" : `${one(v)}%`);

  const stages = [
    { label: "CHECK-INS SENT", value: n(funnel.sent), color: "#00a7a6", sub: "#ccfffe", width: 190 },
    { label: "RESPONDED", value: n(funnel.responded), color: "#00b8b7", sub: "#ccfffe", width: 160 },
    { label: "RISK FLAGS", value: n(funnel.flagged), color: "#d97706", sub: "#ffe8c2", width: 130 },
    { label: "REVIEWED", value: n(funnel.reviewed), color: "#16a34a", sub: "#bbf7d0", width: 104 },
  ];
  const maxStage = Math.max(1, ...stages.map((s) => s.value));

  return (
    <div>
      {/* divider */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "4px 0", marginTop: 12 }}>
        <div style={{ flex: 1, height: 1, background: "linear-gradient(to right,#00a7a640,#e1e0e0)" }} />
        <div style={{ background: "#252424", color: "#00a7a6", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", padding: "6px 16px", borderRadius: 20, border: "1px solid #00a7a640", whiteSpace: "nowrap" }}>
          MaxAlly · Post-Discharge Early Warning
        </div>
        <div style={{ flex: 1, height: 1, background: "linear-gradient(to left,#00a7a640,#e1e0e0)" }} />
      </div>

      {/* KPI row */}
      {/* 6 KPIs laid out 3x2. A single row of 6 is too cramped, and 5-across leaves the sixth
          card stranded alone on a second row; 3x2 also fits the portrait print layout. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginTop: 12 }}>
        <MaKpi
          label="Caregiver Activation"
          value={ca.pct == null ? "—" : one(ca.pct)}
          unit={ca.pct == null ? "" : "%"}
          sub={`${n(ca.activated)} of ${n(ca.invited)} invited`}
        />
        <MaKpi
          label="Check-ins Completed"
          value={n(ci.completed)}
          sub={`of ${n(ci.sent)} sent · ${pctTxt(ci.pct)} response`}
        />
        <MaKpi
          label="Risk Flags Raised"
          value={n(flags.count)}
          sub={
            <span>
              <span style={{ color: "#dc2626", fontWeight: 700 }}>{n(flags.high)} High</span>
              {" · "}
              <span style={{ color: "#d97706", fontWeight: 700 }}>{n(flags.medium)} Medium</span>
            </span>
          }
          foot={`across ${n(flags.patients)} patients`}
          color="#d97706"
        />
        <MaKpi
          label="Flags Reviewed"
          value={n(reviewed.count)}
          sub={`of ${n(flags.count)} · ${pctTxt(reviewed.pct)} opened`}
          foot={reviewed.avgHoursToOpen == null ? null : `avg ${one(reviewed.avgHoursToOpen)} hr to open`}
          color="#16a34a"
        />
        <MaKpi
          label="Coverage Window"
          value={coverage.avgEngagedDays == null ? "—" : one(coverage.avgEngagedDays)}
          unit={coverage.avgEngagedDays == null ? "" : " days"}
          sub="avg days to first touch"
          foot={`across ${n(coverage.patients)} patients`}
        />
        {/* Questions the assistant answered so the facility didn't have to field them.
            Averaged over patients whose caregivers actually used chat, not all patients in scope. */}
        <MaKpi
          label="Questions per Patient"
          value={questions.patients ? one(questions.avgPerPatient) : "—"}
          sub={`${n(questions.total)} questions asked`}
          foot={`across ${n(questions.patients)} patients`}
          color="#00a7a6"
        />
      </div>

      {/* Prevention funnel */}
      <div style={{ background: "#fff", border: "1px solid #e1e0e0", borderRadius: 14, boxShadow: "0 1px 4px rgba(0,0,0,0.07)", padding: 24, marginTop: 16 }}>
        <SectionAccent
          title="Post-Discharge Prevention Funnel"
          sub="Visibility you don't otherwise have → flags surfaced → flags reviewed by your team."
        />
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 0 }}>
          {stages.map((s, i) => (
            <React.Fragment key={s.label}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div style={{ width: s.width, background: s.color, height: 52 + Math.round((s.value / maxStage) * 80), borderRadius: "8px 8px 0 0", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                  <div style={{ color: "#fff", fontSize: "1.6rem", fontWeight: 800 }}>{s.value}</div>
                  <div style={{ color: s.sub, fontSize: "0.72rem", fontWeight: 600 }}>{s.label}</div>
                </div>
                <div style={{ width: 0, height: 0, borderLeft: `${s.width / 2}px solid transparent`, borderRight: `${s.width / 2}px solid transparent`, borderTop: `18px solid ${s.color}` }} />
              </div>
              {i < stages.length - 1 && (
                <div style={{ color: "#00a7a6", fontSize: "1.2rem", margin: "0 4px", paddingBottom: 18 }}>→</div>
              )}
            </React.Fragment>
          ))}
        </div>
        <div style={{ marginTop: 16, textAlign: "center" }}>
          <span style={{ background: "#dcfce7", color: "#15803d", borderRadius: 6, padding: "4px 12px", fontSize: "0.78rem", fontWeight: 600 }}>
            {n(funnel.reviewed)} at-risk patients your team reviewed in time — surfaced by MaxAlly and pushed via the daily risk email
          </span>
        </div>
      </div>

      {/* What the flags caught */}
      <div style={{ background: "#fff", border: "1px solid #e1e0e0", borderRadius: 14, boxShadow: "0 1px 4px rgba(0,0,0,0.07)", padding: 24, marginTop: 16 }}>
        <SectionAccent
          title="What the Flags Caught"
          sub="Risk signals by survey domain · the early warnings that predict a readmit"
        />
        {domains.length === 0 ? (
          <div style={{ fontSize: "0.82rem", color: "#6b6969" }}>No post-discharge risk flags in scope yet.</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={maTh}>Signal Category</th>
                <th style={{ ...maTh, textAlign: "right" }}>Flags</th>
                <th style={{ ...maTh, textAlign: "right" }}>Reviewed</th>
                <th style={{ ...maTh, width: 140 }}>Review Rate</th>
              </tr>
            </thead>
            <tbody>
              {domains.map((d) => {
                const rate = d.count ? Math.round((d.reviewed / d.count) * 100) : 0;
                const rColor = rate >= 75 ? "#16a34a" : rate >= 40 ? "#d97706" : "#6b6969";
                return (
                  <tr key={d.domainName}>
                    <td style={{ ...maTd, fontWeight: 600 }}>{d.domainName}</td>
                    <td style={{ ...maTd, textAlign: "right" }}>{d.count}</td>
                    <td style={{ ...maTd, textAlign: "right", color: rColor, fontWeight: 600 }}>{d.reviewed}</td>
                    <td style={maTd}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 70, height: 6, background: "#e1e0e0", borderRadius: 999, overflow: "hidden" }}>
                          <div style={{ width: `${rate}%`, height: "100%", background: rColor, borderRadius: 999 }} />
                        </div>
                        <span style={{ fontSize: "0.78rem", fontWeight: 600, color: rColor }}>{rate}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* "Why this protects the bottom line" (Modeled $ tie-in) — hidden for now; restore to re-enable
      <div style={{ position: "relative", background: "linear-gradient(135deg,#f0fffe 0%,#eafffe 100%)", border: "1px solid #99eeed", borderRadius: 14, padding: 20, marginTop: 16 }}>
        <div style={{ position: "absolute", top: 10, right: 12, fontSize: "0.6rem", background: "#fef3c7", color: "#92400e", borderRadius: 3, padding: "1px 5px", fontWeight: 700 }}>MODELED</div>
        <div className="section-label" style={{ marginBottom: 12 }}>Why this protects the bottom line</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
          <div style={{ background: "#fff", border: "1px solid #e1e0e0", borderTop: "3px solid #16a34a", borderRadius: 14, padding: 16, textAlign: "center" }}>
            <div style={{ fontSize: "2.1rem", fontWeight: 800, color: "#00a7a6", lineHeight: 1 }}>{n(reviewed.count)}</div>
            <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#252424", marginTop: 2 }}>at-risk patients reviewed in time</div>
            <div style={{ fontSize: "0.72rem", color: "#6b6969", marginTop: 4 }}>flags opened before escalation</div>
          </div>
          <div style={{ background: "#fff", border: "1px solid #e1e0e0", borderTop: "3px solid #00a7a6", borderRadius: 14, padding: 16, textAlign: "center" }}>
            <div style={{ fontSize: "2.1rem", fontWeight: 800, color: "#00a7a6", lineHeight: 1 }}>$9K–$15K</div>
            <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#252424", marginTop: 2 }}>modeled penalty exposure protected</div>
            <div style={{ fontSize: "0.72rem", color: "#6b6969", marginTop: 4 }}>per facility / yr · range, not a guarantee</div>
          </div>
          <div style={{ background: "#fff", border: "1px solid #e1e0e0", borderTop: "3px solid #00a7a6", borderRadius: 14, padding: 16, textAlign: "center" }}>
            <div style={{ fontSize: "2.1rem", fontWeight: 800, color: "#00a7a6", lineHeight: 1 }}>
              {coverage.avgEngagedDays == null ? "—" : one(coverage.avgEngagedDays)}<span style={{ fontSize: "1rem" }}> days</span>
            </div>
            <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#252424", marginTop: 2 }}>avg coverage of the 30-day window</div>
            <div style={{ fontSize: "0.72rem", color: "#6b6969", marginTop: 4 }}>the window CMS actually penalizes on</div>
          </div>
        </div>
        <div style={{ marginTop: 10, fontSize: "0.72rem", color: "#6b6969", background: "#ffffffcc", borderRadius: 6, padding: "8px 14px" }}>
          Dollar figure is a modeled range based on avoided-readmit assumptions × CMS VBP penalty exposure — shown as a
          range and labeled MODELED. Do not present as realized savings.
        </div>
      </div>
      */}
    </div>
  );
}

const maTh = { fontSize: "0.72rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#7b7979", padding: "8px 12px", borderBottom: "1px solid #e1e0e0", textAlign: "left" };
const maTd = { padding: "9px 12px", borderBottom: "1px solid #f4f3f3", fontSize: "0.875rem" };

function MaKpi({ label, value, unit, sub, foot, color = "#00a7a6" }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e1e0e0", borderTop: `3px solid ${color}`, borderRadius: 14, padding: 16, textAlign: "center" }}>
      <div className="section-label">{label}</div>
      <div style={{ fontSize: "2.1rem", fontWeight: 800, color, lineHeight: 1, marginTop: 4 }}>
        {value}{unit ? <span style={{ fontSize: "1.1rem" }}>{unit}</span> : null}
      </div>
      <div style={{ fontSize: "0.78rem", color: "#6b6969", marginTop: 4 }}>{sub}</div>
      {foot ? <div style={{ marginTop: 4, fontSize: "0.72rem", color: "#16a34a", fontWeight: 600 }}>{foot}</div> : null}
    </div>
  );
}

/* ---------- small helpers ---------- */

function Card2({ children }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e1e0e0",
        borderRadius: 14,
        boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
        padding: 24,
        // Clip full-width children (e.g. the Facility Network "Dark/Onboarding" row background)
        // to the rounded boundary so they can't bleed past the card edge.
        overflow: "hidden",
      }}
    >
      {children}
    </div>
  );
}

function SectionAccent({ title, sub }) {
  return (
    <div style={{ borderLeft: "3px solid #00a7a6", paddingLeft: 12, marginBottom: 16 }}>
      <div style={{ fontSize: "1.05rem", fontWeight: 800, letterSpacing: "-0.02em", color: "#1a1919" }}>{title}</div>
      {sub && <div style={{ fontSize: "0.78rem", color: "#6b6969" }}>{sub}</div>}
    </div>
  );
}

function rankMedal(i) {
  if (i === 0) return <span style={{ color: "#b45309", fontWeight: 700 }}>🥇</span>;
  if (i === 1) return <span style={{ color: "#64748b", fontWeight: 700 }}>🥈</span>;
  if (i === 2) return <span style={{ color: "#92400e", fontWeight: 700 }}>🥉</span>;
  return <span style={{ color: "#6b6969", fontWeight: 600 }}>{i + 1}</span>;
}

function pctOf(num, denom) {
  if (!denom) return "—";
  return `${Math.round((num / denom) * 1000) / 10}%`;
}

// Duration display: keep minutes when <= 60 (an hour), otherwise convert to hours (1 decimal).
// minsNum/minsUnit return the number + unit separately (for split value/unit displays like BigStat);
// fmtMinutes returns the combined "45 min" / "25.1 hr" string.
function minsNum(mins) {
  if (mins == null) return "—";
  return mins <= 60 ? mins : Math.round((mins / 60) * 10) / 10;
}
function minsUnit(mins) {
  return mins != null && mins > 60 ? "hr" : "min";
}
function fmtMinutes(mins) {
  if (mins == null) return "—";
  return `${minsNum(mins)} ${minsUnit(mins)}`;
}

function formatDate(d) {
  if (!d) return "";
  return `${d.toLocaleString("en-US", { month: "short" })} ${d.getDate()}, ${d.getFullYear()}`;
}
